import { useMemo } from 'react';

import { useSession } from '@/context/SessionContext';
import { useTenantFeatureMap } from './useTenantFeatureMap';

const GLOBAL_MAPPING_PERMISSIONS = ['platform.mapping.manage'];
const TENANT_MAPPING_PERMISSION = 'tenant.mapping.use';

export interface MappingAccessState {
  featureMap?: Record<string, boolean>;
  featureTenantId?: string;
  mappingFeatureEnabled: boolean;
  mappingPermissionGranted: boolean;
  accessEnabled: boolean;
}

const normalize = (value: string) => value.trim().toLowerCase();

export function useMappingAccess(): MappingAccessState {
  const { session } = useSession();
  const { featureMap, featureTenantId } = useTenantFeatureMap();

  return useMemo(() => {
    const mappingEnabled = !!featureMap?.['mapping.runtime.enabled'];

    const permissionSummary = session?.permissionSummary;
    const scopes = session?.scopes ?? [];

    const hasGlobalPermission = permissionSummary?.global?.some(perm =>
      GLOBAL_MAPPING_PERMISSIONS.some(target => normalize(target) === normalize(perm))
    );

    const hasTenantPermission = featureTenantId
      ? permissionSummary?.tenants?.[featureTenantId]?.some(
          perm => normalize(perm) === TENANT_MAPPING_PERMISSION
        )
      : false;

    const accessFromScopes = scopes.some(scope => {
      const normalized = normalize(scope);
      return (
        normalized === TENANT_MAPPING_PERMISSION ||
        GLOBAL_MAPPING_PERMISSIONS.some(target => normalized === normalize(target))
      );
    });

    const mappingPermissionGranted = Boolean(hasGlobalPermission || hasTenantPermission || accessFromScopes);

    return {
      featureMap,
      featureTenantId,
      mappingFeatureEnabled: mappingEnabled,
      mappingPermissionGranted,
      accessEnabled: mappingEnabled && mappingPermissionGranted
    };
  }, [featureMap, featureTenantId, session?.permissionSummary, session?.scopes]);
}
